import { useState } from "react";
import { useLang } from "@/lib/lang";
import {
  connectQarark,
  disconnectQarark,
} from "@/lib/api";
import { queryClient } from "@/lib/cache/client";
import { queryKeys } from "@/lib/cache/queryKeys";
import { useQararkStatus } from "@/hooks/queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import QararkJordanWarning from "@/components/QararkJordanWarning";

export default function QararkSettings({ open, onOpenChange, onConnected }) {
  const { t, lang } = useLang();
  const { data: status = { connected: false } } = useQararkStatus({ enabled: open });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [captchaImage, setCaptchaImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const connect = async () => {
    setLoading(true);
    try {
      await connectQarark({ username, password, captcha: captcha || undefined });
      toast.success(t("qarark_connected"));
      queryClient.setQueryData(queryKeys.qararkStatus, { connected: true, username });
      setCaptchaImage(null);
      setCaptcha("");
      onConnected?.();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.needs_captcha) {
        setCaptchaImage(detail.captcha_image);
        setCaptcha("");
        toast.message(detail.message || t("qarark_captcha_required"));
      } else if (typeof detail === "object" && detail?.error_code === "device_limit") {
        toast.error(t("qarark_device_limit"), { duration: 12000 });
      } else if (typeof detail === "object" && detail?.error_code === "rate_limit") {
        toast.error(t("qarark_rate_limit"));
      } else if (typeof detail === "object" && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const disconnect = async () => {
    try {
      await disconnectQarark();
      queryClient.setQueryData(queryKeys.qararkStatus, { connected: false });
      toast.success(t("qarark_disconnected"));
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md" data-testid="qarark-settings">
        <DialogHeader>
          <DialogTitle className="font-serif-display">{t("qarark_settings")}</DialogTitle>
        </DialogHeader>
        <QararkJordanWarning className="mb-2" />
        {status.connected ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              {t("qarark_connected_as")}: <strong>{status.username}</strong>
            </p>
            <Button
              data-testid="qarark-disconnect-btn"
              onClick={disconnect}
              variant="outline"
              className="rounded-none w-full"
            >
              {t("qarark_disconnect")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">{t("qarark_settings_hint")}</p>
            <Input
              data-testid="qarark-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t("qarark_username")}
              className="rounded-none"
              dir={lang === "ar" ? "rtl" : "ltr"}
            />
            <Input
              data-testid="qarark-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("qarark_password")}
              className="rounded-none"
            />
            {captchaImage && (
              <>
                <img
                  src={`data:image/png;base64,${captchaImage}`}
                  alt="captcha"
                  className="grid-border max-h-16"
                />
                <Input
                  data-testid="qarark-captcha"
                  value={captcha}
                  onChange={(e) => setCaptcha(e.target.value)}
                  placeholder={t("qarark_captcha")}
                  className="rounded-none"
                />
              </>
            )}
            <Button
              data-testid="qarark-connect-btn"
              onClick={connect}
              disabled={loading || !username || !password || (captchaImage && !captcha.trim())}
              className="rounded-none w-full bg-slate-900 hover:bg-slate-800 text-white"
            >
              {loading ? t("streaming") : t("qarark_connect")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
